/**
 * WS chat protocol adapter — frame parsing.
 *
 * The Digital Staff (WS chat) chat WebSocket emits frames like:
 *   session_start / session  → { session_id|sessionId, resumed }
 *   thinking                 → { content? }
 *   chunk                    → { content }        (streaming text delta)
 *   chunk_reset              →                    (discard the partial buffer)
 *   done                     → { full_response? } (final assistant text)
 *   error                    → { message? }
 *
 * Inspired by AG-UI's "typed event stream → reducer → UI state" architecture,
 * this layer normalizes raw frames into a small typed event union so the React
 * layer never touches the wire format. (This is our own WS chat, not AG-UI.)
 */

import { parsePublishOp, type PublishOp } from './publishOps';

export type WsChatEvent =
  | { type: 'session'; sessionId: string | null; resumed: boolean }
  | { type: 'thinking'; text?: string }
  | { type: 'text-delta'; delta: string }
  | { type: 'text-reset' }
  | { type: 'message-complete'; text: string }
  /** `code` is the frame's own error code when it has one (TURN_STALLED, UPSTREAM_RESTARTED,
   *  …) — what turnErrorNotice keys the merchant's sentence on. Absent on uncoded frames. */
  | { type: 'error'; message: string; code?: string }
  /** Credit pool exhausted — backend `{type:'error', code:'CREDIT_EXHAUSTED'}`. */
  | { type: 'credit-exhausted'; message?: string }
  /** Agent invoked a tool (real frame: {type:'tool_call', id, name, args}). */
  | { type: 'tool-call'; id: string | null; name: string; args?: unknown }
  /** Tool finished (real frame: {type:'tool_result', id, name, output}). `output`
   *  may be a string OR a structured object (ACP tools return JSON payloads). */
  | { type: 'tool-result'; id: string | null; name: string; output?: unknown; eventId?: string | null }
  /** Tool needs approval ({type:'approval_request', tool, request_id, ...}). */
  | {
      type: 'tool-approval';
      id: string | null;
      name: string;
      summary?: string;
      timeoutSec?: number;
    }
  /**
   * Agent needs the user to connect a publishing channel — backend
   * `composioConnectionNeedsAuth` event ({toolkit, auth_url, ...}), emitted when
   * a publish/share tool fails for lack of auth. Rendered as a connect card.
   */
  | { type: 'channel-connect'; toolkit: string; authUrl?: string }
  /**
   * A deterministic (non-agent) publish succeeded — synthesize the same
   * RECORD_PUBLISHED_CONTENT turn the agent path emits, so the button publish
   * renders the identical "Published" confirmation card (with the post link).
   */
  | { type: 'record-published'; channel: string; title?: string; externalUrl?: string }
  /**
   * The proxy re-attached this client to a session whose turn is still
   * streaming after a refresh/detach (`{type:'resume', replyInFlight:true}`).
   * Tells the client to keep waiting for the live reply instead of treating the
   * last persisted state as final.
   */
  | { type: 'resume'; replyInFlight: boolean }
  /**
   * The agent entered a node of a predefined task — chatbot-api's `task_step`
   * frame.
   *
   * It arrives the moment a node is ENTERED, before that node's work, so a turn
   * that walks several nodes emits several of these and the merchant watches the
   * work happen instead of staring at a spinner.
   *
   * Everything except `task` and `node` is filled in server-side from the task
   * definition — the agent reports two ids and nothing else, so the label and the
   * position are the real graph rather than something the model asserted. A
   * report the server REFUSED (an illegal hop) produces no frame at all, which is
   * why progress that stops moving is correct behaviour and not a dropped frame.
   */
  | {
      type: 'task-step';
      task: string;
      taskName: string;
      node: string;
      label: string;
      index: number;
      total: number;
      /**
       * WHICH run of `task` this step belongs to. One turn can carry steps of two runs —
       * test 2026-10-28: a promo-plan turn also re-reported an earlier content-publish run's
       * `publish` — and without the id they were drawn as one walk. Absent on the frames the
       * proxy builds itself (the failed / no_artifact / rewound re-statements) and on older
       * servers; matching then falls back to `task` (see splitTaskRuns in runtime.ts).
       */
      runId?: string;
      /** `failed`: this node's work did not happen — see `failedReason` and the parse below. */
      status: 'active' | 'done' | 'failed';
      /**
       * WHICH failure it was, when the server said. Two things the merchant acts on
       * differently: `tool_failed` — something this step needed came back broken, so trying
       * again is the move; `no_artifact` — the step produced nothing at all, so there is
       * nothing to look for and it has to be run again. Absent on older servers (and on
       * every non-failed step), which the checklist falls back for.
       */
      failedReason?: 'tool_failed' | 'no_artifact';
      /**
       * This turn was taken back: the server refused the work it was doing (nothing was
       * saved) and put the run back on the node named here. The rows the turn had already
       * drawn describe work that did not happen, so they go with it.
       */
      rewound?: boolean;
    }
  /**
   * A server-side publish operation changed state — chatbot-api's `publish_op` frame
   * (docs/CONTRACT-publish-ops.md §6). Not part of any turn: the manager merges it into the
   * conversation's publish-op map by `op.id` and never feeds it to the turn reducer.
   * `op` is the validated op object.
   */
  | { type: 'publish-op'; op: PublishOp }
  /** Any other unknown frame — surfaced as a live activity rather than dropped. */
  | { type: 'activity'; name: string; detail?: string };

interface RawFrame {
  type?: string;
  content?: string;
  full_response?: string;
  session_id?: string;
  sessionId?: string;
  resumed?: boolean;
  message?: string;
  /** error sub-code (e.g. CREDIT_EXHAUSTED). */
  code?: string;
  // tool_call / tool_result
  id?: string;
  name?: string;
  args?: unknown;
  output?: unknown;
  /** Proxy-stamped stable frame id (independent of `id`, which correlates call↔result). */
  eventId?: string;
  // approval_request
  tool?: string;
  request_id?: string;
  arguments_summary?: string;
  timeout_secs?: number;
  timeout_sec?: number;
  status?: string;
  // composioConnectionNeedsAuth
  toolkit?: string;
  auth_url?: string;
  // resume (re-attach after refresh/detach)
  replyInFlight?: boolean;
  // task_step
  task?: string;
  taskName?: string;
  node?: string;
  label?: string;
  index?: number;
  total?: number;
  runId?: string;
  failedReason?: string;
  rewound?: boolean;
  [k: string]: unknown;
}

/** Parse one raw WebSocket frame into a normalized WsChatEvent (or null to ignore). */
export function parseFrame(raw: string): WsChatEvent | null {
  let frame: RawFrame;
  try {
    frame = JSON.parse(raw);
  } catch {
    return null;
  }
  return frameToEvent(frame);
}

/**
 * Parse one raw frame plus its proxy routing field. chatbot-api stamps every
 * server→client frame with the conversation's `sessionId` so one socket can
 * carry many conversations; `sessionId` is null on frames from a pre-multiplex
 * backend (the caller falls back to its single active conversation).
 */
export function parseFrameWithSession(raw: string): {
  sessionId: string | null;
  event: WsChatEvent | null;
} {
  let frame: RawFrame;
  try {
    frame = JSON.parse(raw);
  } catch {
    return { sessionId: null, event: null };
  }
  // `sessionId` is the proxy routing field; session frames also carry the
  // conversation id as `session_id`, which frameToEvent surfaces separately.
  const sessionId = typeof frame.sessionId === 'string' && frame.sessionId ? frame.sessionId : null;
  return { sessionId, event: frameToEvent(frame) };
}

/** Map an already-parsed frame object to an WsChatEvent. Exposed for testing. */
export function frameToEvent(frame: RawFrame | null | undefined): WsChatEvent | null {
  if (!frame || typeof frame !== 'object') return null;

  // Some session frames arrive without a `type` (just session_id + resumed).
  if (!frame.type && (frame.session_id || frame.sessionId)) {
    return {
      type: 'session',
      sessionId: frame.session_id ?? frame.sessionId ?? null,
      resumed: !!frame.resumed,
    };
  }

  switch (frame.type) {
    case 'session_start':
    case 'session':
      return {
        type: 'session',
        sessionId: frame.session_id ?? frame.sessionId ?? null,
        resumed: !!frame.resumed,
      };
    case 'thinking':
      return { type: 'thinking', text: frame.content };
    case 'chunk':
      return { type: 'text-delta', delta: frame.content ?? '' };
    case 'chunk_reset':
      return { type: 'text-reset' };
    case 'done':
      return { type: 'message-complete', text: stripToolCallMarkup(frame.full_response ?? '') };
    // A turn the SERVER ended. zeroclaw emits this after its abort endpoint, which is how a
    // card that is waiting on the merchant stops the model from working straight past it:
    // the card is on screen, there is nothing for the model to add, and the turn is over.
    // Nothing went wrong, so it completes the turn exactly like `done` — with no text of its
    // own, keeping whatever had already streamed. Unhandled it would leave the reply "in
    // flight" until the watchdog fires and tells the merchant they were interrupted.
    case 'aborted':
      return { type: 'message-complete', text: stripToolCallMarkup(frame.full_response ?? '') };
    case 'error':
      if (frame.code === 'CREDIT_EXHAUSTED') {
        return { type: 'credit-exhausted', message: frame.message };
      }
      return typeof frame.code === 'string' && frame.code.trim()
        ? { type: 'error', message: frame.message || 'Agent error', code: frame.code.trim() }
        : { type: 'error', message: frame.message || 'Agent error' };
    case 'tool_call':
      return {
        type: 'tool-call',
        id: frame.id ?? null,
        name: frame.name || 'tool',
        args: frame.args,
      };
    case 'tool_result':
      return {
        type: 'tool-result',
        id: frame.id ?? null,
        name: frame.name || 'tool',
        // Keep the output as-is: ZeroClaw/ACP tools return structured JSON objects
        // (e.g. SHOW_BRAND_ANALYSIS_PROPOSAL → {status, proposal:{...}}), not just
        // strings. Coercing non-strings to undefined dropped the payload the rich
        // cards render from. Downstream string-only consumers guard with typeof.
        output: frame.output,
        eventId: frame.eventId ?? null,
      };
    case 'approval_request':
      return {
        type: 'tool-approval',
        id: frame.request_id ?? null,
        name: frame.tool || frame.name || 'tool',
        summary: frame.arguments_summary || undefined,
        timeoutSec: frame.timeout_secs ?? frame.timeout_sec,
      };
    // Agent (or a failed publish) asks the user to connect a channel.
    case 'composioConnectionNeedsAuth': {
      const toolkit = typeof frame.toolkit === 'string' ? frame.toolkit.trim() : '';
      if (!toolkit) return null;
      return {
        type: 'channel-connect',
        toolkit: toolkit.toLowerCase(),
        authUrl: typeof frame.auth_url === 'string' ? frame.auth_url : undefined,
      };
    }
    // Re-attach after refresh/detach: the turn is still streaming upstream.
    case 'resume':
      return { type: 'resume', replyInFlight: !!frame.replyInFlight };
    case 'task_step': {
      // Guard the whole shape before rendering any of it: this frame is
      // synthesized by the proxy from a tool result, and a progress bar missing
      // its position or its label is worse than no progress bar — it reads as
      // "something is broken" rather than "nothing is happening".
      if (
        typeof frame.task !== 'string' || !frame.task ||
        typeof frame.node !== 'string' || !frame.node ||
        typeof frame.label !== 'string' || !frame.label ||
        typeof frame.index !== 'number' || !Number.isFinite(frame.index) ||
        typeof frame.total !== 'number' || !Number.isFinite(frame.total) ||
        frame.total < 1
      ) {
        return null;
      }
      return {
        type: 'task-step',
        task: frame.task,
        // Falls back to the id: the display name is cosmetic, the id is not, and
        // dropping the whole step over a missing title would lose real progress.
        taskName: typeof frame.taskName === 'string' && frame.taskName ? frame.taskName : frame.task,
        node: frame.node,
        label: frame.label,
        index: frame.index,
        total: frame.total,
        // Undefined rather than '' when absent, like `rewound` below: a step with no id
        // is matched to its run by `task`.
        runId: typeof frame.runId === 'string' && frame.runId ? frame.runId : undefined,
        // `failed` is stamped by the proxy when this node's tools all failed this turn:
        // the agent reported the step anyway, and its label claims something exists that
        // does not. Anything else that is not explicitly terminal is still in flight.
        status:
          frame.status === 'done' ? 'done' : frame.status === 'failed' ? 'failed' : 'active',
        // Only the two the checklist has a sentence for. An unknown code falls back to the
        // generic line rather than rendering a raw identifier at the merchant.
        // Undefined rather than false when absent, so an ordinary step stays the
        // shape it has always been.
        rewound: frame.rewound === true ? true : undefined,
        failedReason:
          frame.failedReason === 'tool_failed' || frame.failedReason === 'no_artifact'
            ? frame.failedReason
            : undefined,
      };
    }
    case 'publish_op': {
      const op = parsePublishOp(frame.op);
      // A `deleted` row is never sent (contract §1); a malformed one is not drawn.
      if (!op || op.deleted) return null;
      return { type: 'publish-op', op };
    }
    // Echo of the user's own message — we already render it locally on send.
    case 'message':
      return null;
    // Start-of-turn lifecycle frame; visible progress is handled by thinking/tool frames.
    case 'agent_start':
      return null;
    // End-of-turn control frame; the visible reply is already finalized by `done`.
    case 'agent_end':
      return null;
    default: {
      if (!frame.type) return null;
      const detail =
        typeof frame.content === 'string' && frame.content.trim() ? frame.content : undefined;
      if (!detail && typeof frame.message === 'string' && frame.message.trim()) {
        return { type: 'error', message: frame.message };
      }
      if (!detail && isTerminalStatus(frame.status)) return null;
      const status = typeof frame.status === 'string' ? frame.status : undefined;
      if (!detail && !status) return null;
      return { type: 'activity', name: frame.type, detail: detail ?? status };
    }
  }
}

interface ToolCallChunkFilterState {
  mode: 'visible' | 'hidden';
  buffer: string;
}

/**
 * Some upstreams leak serialized tool-call markup through normal text chunks
 * before sending the structured `tool_call` frame. Keep that transport detail
 * out of the user-visible stream while preserving any text around it.
 */
export function createToolCallChunkFilter(): (
  event: WsChatEvent,
  sessionId: string | null
) => WsChatEvent | null {
  const states = new Map<string, ToolCallChunkFilterState>();
  const keyFor = (sessionId: string | null) => sessionId || '__default__';

  return (event, sessionId) => {
    const key = keyFor(sessionId);

    if (event.type !== 'text-delta') {
      if (
        event.type === 'tool-call' ||
        event.type === 'message-complete' ||
        event.type === 'error'
      ) {
        states.delete(key);
      }
      return event;
    }

    const state = states.get(key) ?? { mode: 'visible' as const, buffer: '' };
    state.buffer += event.delta;
    const delta = flushToolCallMarkup(state);
    if (state.buffer || state.mode === 'hidden') {
      states.set(key, state);
    } else {
      states.delete(key);
    }
    return delta ? { type: 'text-delta', delta } : null;
  };
}

const TOOL_CALL_OPEN = '<tool_call>';
const TOOL_CALL_CLOSE = '</tool_call>';

/**
 * Remove a thinking model's reasoning markup from assistant text.
 *
 * Measured on dev 2026-09-08 (qwen3.7-plus via LiteLLM): the reasoning itself
 * travels on the provider's `reasoning` channel, which zeroclaw routes to the
 * thinking trace — but the closing `</think>` sentinel leaked into the content
 * stream, so the merchant's bubble opened with a bare `</think>` line. zeroclaw's
 * own stream stripper only reacts to an OPENING tag (stream_guard.rs), and
 * chatbot-api forwards and persists frames verbatim, so this is the last line.
 *
 * Three shapes, in order:
 *  1. a whole `<think>…</think>` block — drop it;
 *  2. an opener whose closer has not streamed yet — drop to the end;
 *  3. an orphan closer with nothing but whitespace before it — the opener rode
 *     the reasoning channel, so only the tag itself is on this side. Anchored
 *     on purpose: prose that merely mentions `</think>` mid-sentence is left
 *     alone rather than losing everything before the mention.
 *
 * Runs on accumulated text (live bubble and history alike), so a tag split
 * across two chunks is whole by the time it gets here.
 */
export function stripThinkBlocks(text: string): string {
  if (!text) return '';
  return text
    .replace(/<think\b[^>]*>[\s\S]*?<\/think\s*>/gi, '')
    .replace(/<think\b[^>]*>[\s\S]*$/gi, '')
    .replace(/^\s*<\/think\s*>\s*/i, '');
}

/**
 * Remove serialized tool markup from final assistant text. The streaming path
 * filters chunk-by-chunk, but some upstreams also copy the same markup into
 * `done.full_response` or persisted history.
 */
export function stripToolCallMarkup(text: string): string {
  if (!text) return '';
  // Reasoning first: a tool-call fragment that sat inside the think block goes
  // with the block instead of being parsed as a tool call of its own.
  return stripThinkBlocks(text)
    .replace(/<tool_call\b[^>]*>[\s\S]*?<\/tool_call>/gi, '')
    .replace(/<tool_result\b[^>]*>[\s\S]*?<\/tool_result>/gi, '')
    .replace(/<tool_call\b[^>]*>[\s\S]*$/gi, '')
    .replace(/<tool_result\b[^>]*>[\s\S]*$/gi, '')
    // Orphan close tags whose opener was already consumed as a structured frame:
    // drop the tag only, never the prose before it.
    .replace(/<\/tool_call>/gi, '')
    .replace(/<\/tool_result>/gi, '')
    // Namespaced internal tool identifiers (e.g. `yepairag_marketing__ASK_USER_QUESTION`)
    // that leak into the agent's prose — never user-facing.
    .replace(/\b[\w]+__[A-Z][A-Z0-9_]{2,}\b/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * 任务在跑的那一轮,把「源码」从要渲染的正文里整段拿掉。
 *
 * 为什么需要它,而 `stripToolCallMarkup` 不够:主 agent 走的是 **XML 文本协议**,不是原生
 * function calling —— 它的工具调用天生就是一段会流进正文的文本。`stripToolCallMarkup`
 * 认的是 `<tool_call>` / `<tool_result>` 这两种**具体形状**,而模型换个写法就绕过去了:
 * 把同一段包进 ``` 围栏、写成别的标签名、或者干脆在思考时把参数 JSON 打出来。
 * 2026-08 换 gpt-5.6-luna 之后就是这么漏成可见文本的。
 *
 * 所以这里不认形状,只认「这看起来是代码」:围栏、标签,以及**流到一半还没闭合的围栏**——
 * 直播时那才是常态,等收尾标记到了再删就已经在屏幕上了。
 *
 * 只用在任务运行中的回合:那些回合的正文本来就该是人话,商家没有任何理由在那里看到代码。
 * 不在这个窗口之外用 —— 商家真要一段代码时(问集成、要 script 片段),得给得出来。
 */
export function stripCodeSurfaces(text: string): string {
  if (!text) return '';
  return text
    // 闭合的围栏
    .replace(/```[\s\S]*?```/g, '')
    // 还没等到收尾的围栏 —— 直播时的常态
    .replace(/```[\s\S]*$/g, '')
    .replace(/^~~~[\s\S]*?^~~~/gm, '')
    .replace(/^~~~[\s\S]*$/gm, '')
    // 成对的标签**连里面的内容一起**删。只删标签是不够的:工具调用的参数值(整篇正文、
    // 一个 id)就夹在标签之间,标签没了内容还在,照样是泄露。
    .replace(/<([a-zA-Z][\w:-]*)(\s[^<>]*)?>[\s\S]*?<\/\1>/g, '')
    // 开了还没闭的标签 —— 直播时的常态 —— 从它开始到结尾全部不渲染。
    .replace(/<[a-zA-Z][\w:-]*(\s[^<>]*)?>[\s\S]*$/g, '')
    // 落单的标签(开头已经被当成结构化帧吃掉了)。要求 `<` 后紧跟字母,所以散文里的
    // "a < b" 不受影响;属性段必须以空格开头,所以 markdown 自动链接 <https://…> 留得住。
    .replace(/<\/?[a-zA-Z][\w:-]*(\s[^<>]*)?\/?>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Strip inline Markdown emphasis so reasoning / "thinking" trace text reads as
 * plain prose. The thinking panel and trace steps render raw text (NOT through
 * the Markdown renderer), so `**Audience**` would otherwise show its literal
 * asterisks to the user. Removes bold/italic/strikethrough/inline-code markers,
 * link syntax, and leading heading/blockquote markers — keeping the words.
 *
 * ONLY for non-rendered surfaces (thinking trace, status). NEVER run this on a
 * message body that goes through the Markdown renderer — it would flatten real
 * formatting.
 */
export function stripInlineMarkdown(text: string): string {
  if (!text) return '';
  return text
    // inline code `x` → x
    .replace(/`([^`]+)`/g, '$1')
    // bold-italic ***x*** → x
    .replace(/\*\*\*([^*]+?)\*\*\*/g, '$1')
    // bold **x** / __x__ → x
    .replace(/\*\*([^*]+?)\*\*/g, '$1')
    .replace(/__([^_]+?)__/g, '$1')
    // italic *x* → x (paired, single line)
    .replace(/\*([^*\n]+?)\*/g, '$1')
    // italic _x_ → x, but leave snake_case identifiers alone
    .replace(/(^|[^A-Za-z0-9_])_([^_\n]+?)_(?![A-Za-z0-9_])/g, '$1$2')
    // strikethrough ~~x~~ → x
    .replace(/~~([^~]+?)~~/g, '$1')
    // links [text](url) → text
    .replace(/\[([^\]]+?)\]\([^)]*\)/g, '$1')
    // leading heading (#) / blockquote (>) markers
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '');
}

function flushToolCallMarkup(state: ToolCallChunkFilterState): string {
  let visible = '';

  while (state.buffer) {
    if (state.mode === 'visible') {
      const open = state.buffer.indexOf(TOOL_CALL_OPEN);
      const close = state.buffer.indexOf(TOOL_CALL_CLOSE);

      // An opener comes first → emit the text before it, then go hidden.
      if (open >= 0 && (close < 0 || open < close)) {
        visible += state.buffer.slice(0, open);
        state.buffer = state.buffer.slice(open + TOOL_CALL_OPEN.length);
        state.mode = 'hidden';
        continue;
      }

      // A close tag with no matching opener (its opener arrived as a structured
      // tool-call frame). Drop the tag, keep the surrounding prose, stay visible.
      if (close >= 0) {
        visible += state.buffer.slice(0, close);
        state.buffer = state.buffer.slice(close + TOOL_CALL_CLOSE.length);
        continue;
      }

      // No complete token. Hold back the longest tail that could be the start of
      // EITHER token so a tag split across chunk boundaries is never leaked.
      const keep = Math.max(
        longestSuffixPrefixLength(state.buffer, TOOL_CALL_OPEN),
        longestSuffixPrefixLength(state.buffer, TOOL_CALL_CLOSE),
      );
      visible += state.buffer.slice(0, state.buffer.length - keep);
      state.buffer = state.buffer.slice(state.buffer.length - keep);
      break;
    }

    const end = state.buffer.indexOf(TOOL_CALL_CLOSE);
    if (end >= 0) {
      state.buffer = state.buffer.slice(end + TOOL_CALL_CLOSE.length);
      state.mode = 'visible';
      continue;
    }

    const keep = longestSuffixPrefixLength(state.buffer, TOOL_CALL_CLOSE);
    state.buffer = state.buffer.slice(state.buffer.length - keep);
    break;
  }

  return visible;
}

function longestSuffixPrefixLength(value: string, token: string): number {
  const max = Math.min(value.length, token.length - 1);
  for (let len = max; len > 0; len -= 1) {
    if (value.endsWith(token.slice(0, len))) return len;
  }
  return 0;
}

function isTerminalStatus(value: unknown): boolean {
  return (
    value === 'failed' ||
    value === 'complete' ||
    value === 'completed' ||
    value === 'done' ||
    value === 'success' ||
    value === 'cancelled' ||
    value === 'canceled'
  );
}
